import {
  OUTREACH_OUTLIER_OTHER_TEAM_DAYS,
  type OutreachOutlierDetail,
  type OutreachOutlierGroup,
  type OutreachOutlierRow,
  type OutreachOutlierSummary,
} from '@aegislab/shared';

/**
 * 「먼저 볼 것」 묶음의 문구와 셈 — 승인 시안 CandidatesAfter(2026-09-24).
 *
 * 판정은 서버가 한다. 여기는 서버가 준 묶음을 사람이 읽는 말로 옮기고, 받은 뒤 화면에서 뺀 수를
 * 겹쳐 세는 것만 한다.
 */

const n = new Intl.NumberFormat('ko-KR');

function day(iso: string | null): string {
  if (iso === null) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : `${date.getMonth() + 1}월 ${date.getDate()}일`;
}

/**
 * `counts` 는 묶음 줄에 붙는 사유별 건수, `note` 는 펼친 본문 첫 줄의 설명이다. 둘을 한 줄에 싣던
 * 때는 줄이 두 겹이 되어 후보 선택 화면이 예산 높이를 넘겼다(퀘스트 2f546c56).
 */
export function groupCopy(group: OutreachOutlierGroup): {
  title: string;
  counts: string | null;
  note: string | null;
} {
  const b = (key: string) => n.format(group.breakdown[key] ?? 0);
  switch (group.code) {
    case 'DO_NOT_SEND':
      return {
        title: '보내면 안 되는 사람',
        counts: `섭외 차단 ${b('OUTREACH_BLOCKED')} · 보류 ${b('HELD')}`,
        note: null,
      };
    case 'OTHER_TEAM_RECENT':
      return {
        title: '다른 팀이 최근 연락함',
        counts: null,
        note: `${OUTREACH_OUTLIER_OTHER_TEAM_DAYS}일 안에 다른 팀이 연락했거나 진행 중인 사람입니다.`,
      };
    case 'ALREADY_ANSWERED':
      return {
        title: '이 캠페인에서 이미 답함',
        counts: `회신 ${b('replied')} · 폼 제출 ${b('form_submitted')}`,
        note: null,
      };
    case 'SHARED_ADDRESS':
      return {
        title: '주소 하나를 여러 계정이 씀',
        counts: `주소 ${b('ADDRESSES')}개`,
        note: '기본은 주소 하나에 한 통만 보냅니다.',
      };
    case 'NO_ADDRESS':
      return {
        title: '보낼 주소가 없음',
        counts: `주소 없음 ${b('NO_EMAIL')} · 원천에서 못 찾음 ${b('UNRESOLVED')}`,
        note: null,
      };
  }
}

/** 기본 처리 칩 문구. 되돌릴 수 없는 묶음은 잠금 아이콘이 옆에 붙는다. */
export function actionLabel(group: OutreachOutlierGroup): string {
  switch (group.defaultAction) {
    case 'EXCLUDE':
      return group.reversible ? '제외' : '제외 · 되돌릴 수 없음';
    case 'INCLUDE':
      return '포함 · 확인 필요';
    case 'ONE_PER_ADDRESS':
      return '한 통으로';
    case 'UNSENDABLE':
      return '발송 불가';
  }
}

/** 기본적으로 나가지 않는 묶음 — 하단 「뺄 계정」 에 든다. */
export function isDroppedByDefault(group: OutreachOutlierGroup): boolean {
  return group.defaultAction === 'EXCLUDE' || group.defaultAction === 'UNSENDABLE';
}

const OTHER_TEAM_EVENT: Record<string, string> = {
  CONTACT_SENT: '섭외',
  REPLY_RECEIVED: '회신',
  ROSTER_JOINED: '로스터 합류',
  PRICE_AGREED: '단가 합의',
  CONTENT_POSTED: '콘텐츠 게시',
};

export function rowDetailText(detail: OutreachOutlierDetail): string {
  switch (detail.code) {
    case 'DO_NOT_SEND':
      return detail.reason === 'OUTREACH_BLOCKED'
        ? `향후 섭외 차단${detail.at ? ` · ${day(detail.at)}` : ''}`
        : `보류${detail.at ? ` · ${day(detail.at)}까지` : ''}`;
    case 'OTHER_TEAM_RECENT':
      return `${detail.teamNames.join('·') || '팀 모름'} · ${day(detail.lastEventAt)} ${
        OTHER_TEAM_EVENT[detail.lastEventType] ?? '연락'
      }`;
    case 'ALREADY_ANSWERED':
      return detail.state === 'replied'
        ? `회신${detail.at ? ` · ${day(detail.at)}` : ''}`
        : '폼 제출';
    case 'SHARED_ADDRESS':
      return detail.sharedWith.some((m) => m.group === 'OTHER_TEAM_RECENT')
        ? '같은 주소 · 상대 계정은 다른 팀 묶음에 있음'
        : '같은 주소';
    case 'NO_ADDRESS':
      return detail.reason === 'NO_EMAIL' ? '주소 없음' : '원천에서 못 찾음';
  }
}

export interface OutlierLine {
  key: string;
  /** 이 줄이 가리키는 계정 — 같은 주소 쌍이면 둘 이상이다. */
  ids: string[];
  label: string;
  detail: string;
}

/**
 * 같은 주소 행은 계정마다 하나라 한 쌍이 두 줄로 온다(a→[b], b→[a]). 계약이 적어 둔 대로
 * `[자기, ...sharedWith]` 집합으로 접어 쌍마다 한 줄로 보인다. 다른 묶음은 그대로 한 줄씩이다.
 */
export function foldSharedRows(rows: readonly OutreachOutlierRow[]): OutlierLine[] {
  const seen = new Set<string>();
  const lines: OutlierLine[] = [];
  for (const row of rows) {
    const members =
      row.detail.code === 'SHARED_ADDRESS'
        ? [
            { externalCreatorId: row.externalCreatorId, handle: row.handle },
            ...row.detail.sharedWith,
          ]
        : [{ externalCreatorId: row.externalCreatorId, handle: row.handle }];
    const key = members
      .map((m) => m.externalCreatorId)
      .sort()
      .join('|');
    if (seen.has(key)) continue;
    seen.add(key);
    lines.push({
      key,
      ids: members.map((m) => m.externalCreatorId),
      label: members.map((m) => m.handle ?? '핸들 모름').join(' · '),
      detail: rowDetailText(row.detail),
    });
  }
  return lines;
}

const baselines = new WeakMap<OutreachOutlierSummary, ReadonlySet<string>>();

/**
 * 이 묶음 응답을 처음 본 때의 제외 목록. 응답 객체에 붙여 두므로 나라를 갔다 와도 같은 응답이면
 * 같은 기준을 쓴다. 서버가 그 응답을 낼 때 본 제외와 같다고 본다 — 저장이 응답보다 늦게 닿는
 * 순간의 어긋남은 다음 응답이 바로잡는다.
 */
export function baselineOf(
  summary: OutreachOutlierSummary,
  excludedNow: ReadonlySet<string>
): ReadonlySet<string> {
  let baseline = baselines.get(summary);
  if (baseline === undefined) {
    baseline = new Set(excludedNow);
    baselines.set(summary, baseline);
  }
  return baseline;
}

/**
 * 하단 「보낼 계정 · 뺄 계정」 과 「먼저 볼 것」 수.
 *
 * 서버는 응답 때의 명단 안 제외를 `manuallyExcludedCount` 로 세고 `totalCount` 에서 이미 뺐다.
 * 제외 목록에는 명단 밖 id(옛 조회에서 뺀 사람)도 들어 있어 그 크기를 그대로 쓰면 보낼 수가 깎인다.
 * 그래서 응답 뒤 **이 화면에서 바뀐 것만** 겹쳐 센다 — 기준(`baseline`)에 없던 제외는 옮기고,
 * 기준에 있다 다시 넣은 사람은 되돌린다. 새로 빼거나 넣는 길(표 행·묶음 행)은 모두 명단 안 사람만
 * 보인다. 기본으로 빠지는 묶음의 사람을 또 빼면 이미 뺄 쪽에 있으므로 세지 않는다.
 * 계정 수이지 통 수가 아니다 — 같은 주소로 접히는 것은 발송 확인 면이 센다.
 */
export function outlierTotals(
  summary: OutreachOutlierSummary,
  baseline: ReadonlySet<string>,
  excludedNow: ReadonlySet<string>
): { review: number; send: number; drop: number } {
  const review = summary.groups.reduce((sum, g) => sum + g.count, 0);
  const droppedGroups = summary.groups.filter(isDroppedByDefault);
  const dropped = droppedGroups.reduce((sum, g) => sum + g.count, 0);
  // ponytail: 기본으로 빠지는 사람은 미리보기 행만 안다. 미리보기 밖 차단·주소 없음 행을 표에서
  // 해제하면 한 번 더 센다 — 서버가 제외된 사람의 묶음을 알려 주면(후행 계약) 그걸로 바꾼다.
  const droppedIds = new Set(
    droppedGroups.flatMap((g) => g.preview.map((row) => row.externalCreatorId))
  );
  let moved = 0;
  for (const id of excludedNow) if (!baseline.has(id) && !droppedIds.has(id)) moved += 1;
  for (const id of baseline) if (!excludedNow.has(id) && !droppedIds.has(id)) moved -= 1;
  return {
    review,
    send: Math.max(0, summary.totalCount - dropped - moved),
    drop: Math.max(0, dropped + summary.manuallyExcludedCount + moved),
  };
}
